import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, InputLabel, Skeleton, Switch, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { CustomInput } from "global/components";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import { contrastRatio } from "utils/accessibility";
import SettingsLayout from "../components/SettingsLayout";
import { useTitle } from "utils/UseTitle";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { WorkspaceBranding as WorkspaceBrandingType } from "types/workspace";
import {
  fetchWorkspaceBranding,
  updateWorkspaceBranding,
  uploadWorkspaceLogo,
} from "./WorkspaceBrandingServices";
import WorkspacesBackLink from "./components/WorkspacesBackLink";
import {
  alertSx,
  buttonPreviewSx,
  buttonRowSx,
  colorChipDotSx,
  colorChipSx,
  colorChipValueSx,
  colorInputSx,
  colorRowSx,
  formCardSx,
  formFieldsSx,
  logoBoxSx,
  logoRowSx,
  sectionDescriptionSx,
  sectionDividerSx,
  sectionHeaderSx,
  sectionHeaderTextSx,
  sectionTitleSx,
} from "./Workspaces.styles";

const DEFAULT_PRIMARY_COLOR = "#2563eb";
const DEFAULT_BUTTON_TEXT_COLOR = "#ffffff";

const emptyBranding: WorkspaceBrandingType = {
  senderDisplayName: "",
  senderEmail: "",
  logoUrl: "",
  primaryColor: DEFAULT_PRIMARY_COLOR,
  buttonTextColor: DEFAULT_BUTTON_TEXT_COLOR,
  footerHtml: "",
  enabled: false,
};

const TOGGLE_LABEL_ID = "workspace-branding-toggle-label";

const WorkspaceBranding = () => {
  useTitle("Workspace branding");
  const { id: workspaceId } = useParams<{ id: string }>();
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);

  const [branding, setBranding] = useState<WorkspaceBrandingType>(emptyBranding);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const load = async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const data = await fetchWorkspaceBranding(workspaceId);
      setBranding({ ...emptyBranding, ...(data || {}) });
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Do NOT dispatch setActiveWorkspace here: viewing a workspace's branding settings
    // must not silently switch the user's active workspace (which drives X-Workspace-Id
    // and every hasWorkspacePermission check) for the rest of the session. The branding
    // service calls take an explicit workspaceId, so no global state change is needed.
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const set = (patch: Partial<WorkspaceBrandingType>) =>
    setBranding((prev) => ({ ...prev, ...patch }));

  // WCAG 2.1 AA: the signing-page action button renders buttonTextColor on primaryColor,
  // and the button sits on a white page. Guard both so an admin cannot ship a low-contrast pair.
  const primary = branding.primaryColor || DEFAULT_PRIMARY_COLOR;
  const btnText = branding.buttonTextColor || DEFAULT_BUTTON_TEXT_COLOR;
  const textRatio = contrastRatio(btnText, primary);
  const pageRatio = contrastRatio(primary, "#ffffff");
  const textPassesAA = textRatio >= 4.5; // normal text
  const buttonVisibleOnPage = pageRatio >= 3; // non-text (1.4.11)
  // Only block saving when branding is actually enabled — the colors are unused otherwise.
  const contrastBlocksSave = branding.enabled && !textPassesAA;

  const onLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Client-side guards for faster feedback than a server rejection.
    if (!file.type.startsWith("image/")) {
      openErrorNotification("Please choose an image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      openErrorNotification("Logo must be 2 MB or smaller.");
      return;
    }
    try {
      setUploading(true);
      const form = new FormData();
      form.append("file", file);
      const url = await uploadWorkspaceLogo(form);
      if (url) {
        set({ logoUrl: url });
        openSuccessNotification("Logo uploaded.");
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!workspaceId) return;
    if (contrastBlocksSave) {
      openErrorNotification(
        `Button text and primary color contrast is ${textRatio.toFixed(
          1
        )}:1 — WCAG AA needs at least 4.5:1. Adjust the colors before saving.`
      );
      return;
    }
    try {
      setSaving(true);
      await updateWorkspaceBranding(workspaceId, branding);
      openSuccessNotification("Branding saved.");
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSaving(false);
    }
  };

  /** Same label + gap `CustomInput` draws, for the non-input fields. */
  const fieldLabel = (id: string, text: string) => (
    <Box mb={formFieldMetrics.labelGap}>
      <InputLabel id={id} sx={customInputStyles.inputLabel}>
        {text}
      </InputLabel>
    </Box>
  );

  /** Hex read-out chip that opens the native colour picker when clicked. */
  const colorField = (
    id: string,
    label: string,
    value: string,
    onPick: (color: string) => void,
  ) => (
    <Box>
      {fieldLabel(`${id}-label`, label)}
      <Box component="label" htmlFor={id} sx={colorChipSx(!isAdmin)}>
        <Box sx={colorChipDotSx(value)} />
        <Box component="span" sx={colorChipValueSx}>
          {value}
        </Box>
        <Box
          component="input"
          id={id}
          type="color"
          aria-labelledby={`${id}-label`}
          disabled={!isAdmin}
          value={value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onPick(event.target.value)
          }
          sx={colorInputSx}
        />
      </Box>
    </Box>
  );

  const renderSkeleton = () => (
    <Box sx={formCardSx} aria-hidden="true">
      <Skeleton variant="text" width="35%" sx={{ fontSize: "15px" }} />
      <Box sx={formFieldsSx}>
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={56} width={160} />
      </Box>
    </Box>
  );

  const renderCard = () => (
    <Box sx={formCardSx}>
      <Box sx={sectionHeaderSx}>
        <Box sx={sectionHeaderTextSx}>
          <Typography component="h2" id={TOGGLE_LABEL_ID} sx={sectionTitleSx}>
            Custom branding
          </Typography>
          <Typography component="div" sx={sectionDescriptionSx}>
            Use this workspace's own sender, logo and colours on its signing
            emails and pages instead of the account's.
          </Typography>
        </Box>
        <Switch
          checked={branding.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          disabled={!isAdmin}
          inputProps={{ "aria-labelledby": TOGGLE_LABEL_ID }}
        />
      </Box>

      <Box sx={formFieldsSx}>
        <CustomInput
          id="workspaceSenderName"
          label="Sender name"
          placeHolder="e.g. Acme Sales"
          name="senderDisplayName"
          value={branding.senderDisplayName ?? ""}
          onChange={(e: any) => set({ senderDisplayName: e.target.value })}
          disabled={!isAdmin}
        />
        <CustomInput
          id="workspaceSenderEmail"
          label="Sender email"
          placeHolder="name@company.com"
          name="senderEmail"
          type="email"
          value={branding.senderEmail ?? ""}
          onChange={(e: any) => set({ senderEmail: e.target.value })}
          disabled={!isAdmin}
        />

        <Box>
          {fieldLabel("workspace-logo-label", "Logo")}
          <Box sx={logoRowSx}>
            <Box sx={logoBoxSx}>
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Workspace logo" />
              ) : (
                "No logo"
              )}
            </Box>
            {isAdmin && (
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={uploading}
              >
                {uploading ? "Uploading…" : branding.logoUrl ? "Replace logo" : "Upload logo"}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  aria-labelledby="workspace-logo-label"
                  onChange={onLogoSelected}
                />
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={sectionDividerSx}>
        <Box sx={formFieldsSx}>
          <Box sx={colorRowSx}>
            {colorField("workspacePrimaryColor", "Primary color", primary, (c) =>
              set({ primaryColor: c }),
            )}
            {colorField("workspaceButtonTextColor", "Button text color", btnText, (c) =>
              set({ buttonTextColor: c }),
            )}
            <Box aria-hidden="true" sx={buttonPreviewSx(primary, btnText)}>
              Review &amp; sign
            </Box>
          </Box>

          {!textPassesAA && (
            <Box role="alert" sx={alertSx(branding.enabled ? "error" : "warning")}>
              {branding.enabled ? <ErrorOutlineIcon /> : <WarningAmberRoundedIcon />}
              <Box component="span">
                Button text vs. primary color contrast is{" "}
                <strong>{textRatio.toFixed(1)}:1</strong>. WCAG 2.1 AA needs at
                least <strong>4.5:1</strong> for button text.
                {branding.enabled
                  ? " Saving is blocked until you fix it."
                  : " Fix it before turning custom branding on."}
              </Box>
            </Box>
          )}
          {textPassesAA && !buttonVisibleOnPage && (
            <Box role="alert" sx={alertSx("warning")}>
              <WarningAmberRoundedIcon />
              <Box component="span">
                The primary color is only <strong>{pageRatio.toFixed(1)}:1</strong>{" "}
                against a white page (WCAG 2.1 AA wants 3:1 for the button
                edge), so the button may be hard to spot.
              </Box>
            </Box>
          )}

          <CustomInput
            id="workspaceFooterHtml"
            label="Email footer (HTML)"
            placeHolder="<p>Acme Inc · 1 Main St</p>"
            name="footerHtml"
            value={branding.footerHtml ?? ""}
            onChange={(e: any) => set({ footerHtml: e.target.value })}
            multiline
            minRows={3}
            disabled={!isAdmin}
          />
        </Box>
      </Box>

      {isAdmin && (
        <Box sx={buttonRowSx}>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || contrastBlocksSave}
          >
            Save branding
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <SettingsLayout activeTab="Workspaces" title="Workspace branding">
      <WorkspacesBackLink />
      {loading ? renderSkeleton() : renderCard()}
    </SettingsLayout>
  );
};

export default WorkspaceBranding;
